import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import '@fontsource-variable/geist/wght.css';
import {
  Timer,
  Route,
  Ticket,
  Lock,
  ChevronRight,
  Settings,
  Wrench,
  Truck,
  Package,
  Send,
  CalendarClock,
  CalendarCheck,
  CheckCircle2,
  ClipboardList,
  Boxes,
  ShieldCheck,
  Gauge,
  Unplug,
  BatteryCharging,
  Store,
  Loader2,
  Flag,
} from 'lucide-react';
import ProcessCfgModal from './components/ProcessCfgModal';
import { Ci2HelpHexIcon } from './components/Ci2HelpHexIcon';
import { useCxExpToast } from '../customer-experience/components/CxExpSonnerToast';
import { fetchCompletionStatus, lookupCompletionStatus, saveCompletionStatus } from './services/completionStatusService';
import { fetchOpeningRecurrence, saveOpeningRecurrence } from './services/openingRecurrenceService';
import './ConfigStatusView2.css';

const PCS2_MAX_STATUS_TAGS = 9;
const PCS2_MAX_TAGS_TOOLTIP = 'Limite de 9 status atingido. Remova um para adicionar outro.';
const RECURRENCE_TYPES = [
  { key: 'corrective', label: 'Corretiva' },
  { key: 'installation', label: 'Instalação' },
  { key: 'uninstallation', label: 'Desinstalação' },
  { key: 'preventive', label: 'Preventiva' },
  { key: 'stock', label: 'Estoque' },
  { key: 'recharge', label: 'Recarga' },
  { key: 'measurement', label: 'Aferição' },
  { key: 'counter', label: 'Balcão' },
];
const RECURRENCE_TYPE_KEYS = RECURRENCE_TYPES.map((t) => t.key);

const sameTypes = (a, b) => [...a].sort().join() === [...b].sort().join();

const PCS2_RETORNO_COND_INFO =
  'Habilita o controle de liberação de pedidos de peça vinculados a ordem de serviço por condição de intervenção, podendo configurar por condição processos de saída e o ponto de gatilho para liberar a OS.';

const PCS2_RECURRENCE_INFO =
  'Reincidência é uma OS nova do mesmo equipamento, aberta depois de outra já fechada, dentro deste prazo e só nos tipos marcados. Não é retorno nem um novo ciclo da mesma OS.';

function normalizeAutomationData(data) {
  if (!data) {
    return {
      day1_sun: false,
      day2_mon: false,
      day3_tue: false,
      day4_wed: false,
      day5_thu: false,
      day6_fri: false,
      day7_sat: false,
      schedule: '',
      tipoagend: 1,
    };
  }
  return {
    day1_sun: !!data.day1_sun,
    day2_mon: !!data.day2_mon,
    day3_tue: !!data.day3_tue,
    day4_wed: !!data.day4_wed,
    day5_thu: !!data.day5_thu,
    day6_fri: !!data.day6_fri,
    day7_sat: !!data.day7_sat,
    schedule: String(data.schedule ?? '').trim(),
    tipoagend: Number(data.tipoagend ?? 1),
  };
}

function normalizeFutureSchedule(value) {
  const trimmed = String(value ?? '').trim();
  return trimmed || '04:30';
}

function serializePcs2FormState({
  statusInservice,
  statusDeslocamento,
  statusRetorno,
  statusForward,
  statusTomorrow,
  statusUptodate,
  statusFinish,
  statusOpenTags,
  retiradaAtivaTags,
  enviarProximoDiaAutomatico,
  futureTomorrowSchedule,
  retornoPorCondicaoIntervencao,
  habilitarTrocaStatus,
  osCorretiva,
  osEstoque,
  osPreventiva,
  osAfericao,
  osDesinstalacao,
  osRecarga,
  osBalcao,
  automationStatusPedidoDisponivel,
  automationStatusOsEncaminhar,
  automationData,
  equipmentSearchType,
}) {
  return JSON.stringify({
    statusInservice: statusInservice ?? '',
    statusDeslocamento: statusDeslocamento ?? '',
    statusRetorno: statusRetorno ?? '',
    statusForward: statusForward ?? '',
    statusTomorrow: statusTomorrow ?? '',
    statusUptodate: statusUptodate ?? '',
    statusFinish: statusFinish ?? '',
    statusOpenTags: [...(statusOpenTags || [])],
    retiradaAtivaTags: [...(retiradaAtivaTags || [])],
    enviarProximoDiaAutomatico: !!enviarProximoDiaAutomatico,
    futureTomorrowSchedule: normalizeFutureSchedule(futureTomorrowSchedule),
    retornoPorCondicaoIntervencao: !!retornoPorCondicaoIntervencao,
    habilitarTrocaStatus: !!habilitarTrocaStatus,
    osCorretiva: osCorretiva ?? '',
    osEstoque: osEstoque ?? '',
    osPreventiva: osPreventiva ?? '',
    osAfericao: osAfericao ?? '',
    osDesinstalacao: osDesinstalacao ?? '',
    osRecarga: osRecarga ?? '',
    osBalcao: osBalcao ?? '',
    automationStatusPedidoDisponivel: automationStatusPedidoDisponivel ?? '',
    automationStatusOsEncaminhar: automationStatusOsEncaminhar ?? '',
    automationData: normalizeAutomationData(automationData),
    equipmentSearchType: equipmentSearchType ?? 'serie-patrimonio',
  });
}

function Pcs2TooltipHost({ text, children, className = '', align = 'start' }) {
  if (!text) return children;
  return (
    <span
      className={[
        'pcs2-tooltip-host',
        align === 'end' ? 'pcs2-tooltip-host--align-end' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      data-tooltip={text}
    >
      {children}
    </span>
  );
}

function Pcs2InfoTip({ text }) {
  return (
    <Pcs2TooltipHost text={text} className="pcs2-info-tip-host">
      <Ci2HelpHexIcon label="Mais informações" />
    </Pcs2TooltipHost>
  );
}

function Pcs2Switch({ checked, onChange, ariaLabel, tooltip }) {
  const inputRef = useRef(null);

  const handleChange = (e) => {
    onChange(e);
    requestAnimationFrame(() => inputRef.current?.blur());
  };

  const switchEl = (
    <label className="pcs2-switch">
      <input
        ref={inputRef}
        type="checkbox"
        checked={checked}
        onChange={handleChange}
        aria-label={ariaLabel}
      />
      <span className="pcs2-switch-pill">
        <span className="pcs2-switch-dot" />
      </span>
    </label>
  );
  return tooltip ? <Pcs2TooltipHost text={tooltip} align="end">{switchEl}</Pcs2TooltipHost> : switchEl;
}

function Pcs2StatusPill({ tag, index, onRemove, onUpdate }) {
  const isDraft = tag === '';
  const [editing, setEditing] = useState(isDraft);
  const [draft, setDraft] = useState(tag);
  const editRef = useRef(null);

  useEffect(() => {
    setDraft(tag);
    if (tag === '') setEditing(true);
  }, [tag]);

  useEffect(() => {
    if (editing) editRef.current?.focus();
  }, [editing]);

  const commitEdit = useCallback(() => {
    const trimmed = draft.trim().toUpperCase();
    if (trimmed.length === 2) {
      onUpdate(index, trimmed);
      setEditing(false);
    } else if (!trimmed) {
      onRemove(index);
    } else {
      setDraft(tag);
      setEditing(false);
    }
  }, [draft, index, onRemove, onUpdate, tag]);

  const pillLabel = tag || 'novo status';

  return (
    <span className={`pcs2-status-pill${isDraft ? ' pcs2-status-pill--draft' : ''}`}>
      {editing ? (
        <input
          ref={editRef}
          type="text"
          className="pcs2-status-pill-code pcs2-status-pill-code--edit"
          value={draft}
          maxLength={2}
          placeholder="00"
          onChange={(e) => setDraft(e.target.value.toUpperCase())}
          onBlur={commitEdit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitEdit();
            }
            if (e.key === 'Escape') {
              if (isDraft) {
                onRemove(index);
              } else {
                setDraft(tag);
                setEditing(false);
              }
            }
          }}
          aria-label={`Editar ${pillLabel}`}
        />
      ) : (
        <button
          type="button"
          className="pcs2-status-pill-code"
          onClick={() => {
            setDraft(tag);
            setEditing(true);
          }}
          aria-label={`Editar status ${tag}`}
        >
          {tag}
        </button>
      )}
      <Pcs2TooltipHost text="Remover">
        <button
          type="button"
          className="pcs2-status-pill-remove"
          onClick={() => onRemove(index)}
          aria-label={`Remover status ${pillLabel}`}
        >
          ×
        </button>
      </Pcs2TooltipHost>
    </span>
  );
}

function Pcs2StatusTagsBlock({
  title,
  desc,
  tags,
  onAddEmpty,
  onRemove,
  onUpdate,
  id,
  maxTags = PCS2_MAX_STATUS_TAGS,
}) {
  const isUnlimited = maxTags == null;
  const hasDraft = tags.some((tag) => tag === '');
  const atMax = !isUnlimited && tags.length >= maxTags;
  const canAdd = (isUnlimited || tags.length < maxTags) && !hasDraft;

  return (
    <div className="pcs2-subblock">
      <div className="pcs2-subhead pcs2-subhead--with-action">
        <div className="pcs2-subhead-copy">
          <h4 className="pcs2-subtitle">{title}</h4>
          {desc ? <p className="pcs2-subdesc">{desc}</p> : null}
        </div>
        <div className="pcs2-subhead-action">
          <Pcs2TooltipHost text={atMax ? PCS2_MAX_TAGS_TOOLTIP : undefined} align="end">
            <button
              type="button"
              className="pcs2-chip-add"
              disabled={!canAdd}
              onClick={onAddEmpty}
              aria-label="Adicionar status"
            >
              + Add
            </button>
          </Pcs2TooltipHost>
        </div>
      </div>
      <div className="pcs2-tags-chips">
        {tags.map((tag, index) => (
          <Pcs2StatusPill
            key={`${id}-pill-${index}`}
            tag={tag}
            index={index}
            onRemove={onRemove}
            onUpdate={onUpdate}
          />
        ))}
      </div>
    </div>
  );
}

function Pcs2Subblock({ title, desc, children, hideHead = false }) {
  return (
    <div className="pcs2-subblock">
      {!hideHead && (title || desc) ? (
        <div className="pcs2-subhead">
          <div className="pcs2-subhead-copy">
            {title ? <h4 className="pcs2-subtitle">{title}</h4> : null}
            {desc ? <p className="pcs2-subdesc">{desc}</p> : null}
          </div>
        </div>
      ) : null}
      {children}
    </div>
  );
}

function Pcs2Section({ icon: Icon, title, desc, children }) {
  return (
    <section className="pcs2-section">
      <div className="pcs2-section-aside">
        <div className="pcs2-section-heading">
          <Icon size={16} strokeWidth={1.75} aria-hidden />
          <h3 className="pcs2-section-title">{title}</h3>
        </div>
        <p className="pcs2-section-desc">{desc}</p>
      </div>
      <div className="pcs2-section-body">{children}</div>
    </section>
  );
}

function Pcs2ListItem({ icon: Icon, label, id, value, onChange, disabled }) {
  return (
    <div className={`pcs2-list-item${disabled ? ' pcs2-list-item--disabled' : ''}`}>
      <span className="pcs2-list-icon">
        <Icon size={12} strokeWidth={1.75} aria-hidden />
      </span>
      <span className="pcs2-list-label">{label}</span>
      <input
        type="text"
        id={id}
        className="pcs2-list-pin"
        maxLength={2}
        value={value}
        onChange={onChange}
        disabled={disabled}
        aria-label={label}
      />
      <ChevronRight className="pcs2-list-chev" aria-hidden />
    </div>
  );
}

function Pcs2SettingRow({ title, desc, descInfo, children }) {
  return (
    <div className="pcs2-setting-row">
      <div className="pcs2-setting-copy">
        <span className="pcs2-setting-title">{title}</span>
        {desc ? (
          <span className="pcs2-setting-desc-row">
            <span className="pcs2-setting-desc">{desc}</span>
            {descInfo ? <Pcs2InfoTip text={descInfo} /> : null}
          </span>
        ) : null}
      </div>
      <div className="pcs2-setting-actions">{children}</div>
    </div>
  );
}

function Pcs2SegmentedControl({ value, onChange, options, disabled, ariaLabel }) {
  return (
    <div className="pcs2-segmented" role="radiogroup" aria-label={ariaLabel}>
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            className={`pcs2-segmented-btn${active ? ' pcs2-segmented-btn--active' : ''}`}
            onClick={() => !disabled && onChange(opt.value)}
            disabled={disabled}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

const WEEK_ROWS = [
  { key: 'day2_mon', label: 'Segunda' },
  { key: 'day3_tue', label: 'Terça' },
  { key: 'day4_wed', label: 'Quarta' },
  { key: 'day5_thu', label: 'Quinta' },
  { key: 'day6_fri', label: 'Sexta' },
  { key: 'day7_sat', label: 'Sábado' },
  { key: 'day1_sun', label: 'Domingo' },
];

/**
 * Processos → Configuração de status 2 (layout horizontal Geist).
 * Mesmo backend/estado do ConfigStatusView (ProcessConfig em App.js).
 */
export default function ConfigStatusView2(props) {
  const {
    saveData,
    isSaving,
    message,
    statusInservice,
    statusDeslocamento,
    statusRetorno,
    statusForward,
    statusTomorrow,
    statusUptodate,
    statusFinish,
    statusOpenTags,
    statusOpenInput,
    statusOpenInputRef,
    enviarProximoDiaAutomatico,
    setEnviarProximoDiaAutomatico,
    futureTomorrowSchedule,
    futureTomorrowScheduleModalOpen,
    setFutureTomorrowScheduleModalOpen,
    handleFutureTomorrowScheduleChange,
    retornoPorCondicaoIntervencao,
    setRetornoPorCondicaoIntervencao,
    habilitarTrocaStatus,
    setHabilitarTrocaStatus,
    osCorretiva,
    osEstoque,
    osPreventiva,
    osAfericao,
    osDesinstalacao,
    osRecarga,
    osBalcao,
    automationStatusPedidoDisponivel,
    automationStatusOsEncaminhar,
    automationData,
    automationError,
    isLoadingAutomation,
    isGeneratingReport,
    automationRetornoModalOpen,
    setAutomationRetornoModalOpen,
    automationProximoDiaModalOpen,
    setAutomationProximoDiaModalOpen,
    handleInserviceChange,
    handleDeslocamentoChange,
    handleRetornoChange,
    handleForwardChange,
    handleTomorrowChange,
    handleUptodateChange,
    handleFinishChange,
    handleOsCorretivaChange,
    handleOsEstoqueChange,
    handleOsPreventivaChange,
    handleOsAfericaoChange,
    handleOsDesinstalacaoChange,
    handleOsRecargaChange,
    handleOsBalcaoChange,
    handleAutomationDayChange,
    handleAutomationScheduleChange,
    handleAutomationTipoagendChange,
    handleAutomationStatusPedidoDisponivel,
    handleAutomationStatusOsEncaminhar,
    validateSchedule,
    validateFutureTomorrowSchedule,
    generateLogsReport,
    addStatusOpenTag,
    addEmptyStatusOpenTag,
    removeStatusOpenTag,
    updateStatusOpenTag,
    handleStatusOpenInput,
    handleStatusOpenKeyDown,
    retiradaAtivaTags,
    retiradaAtivaInput,
    retiradaAtivaInputRef,
    addRetiradaAtivaTag,
    addEmptyRetiradaAtivaTag,
    removeRetiradaAtivaTag,
    updateRetiradaAtivaTag,
    handleRetiradaAtivaInput,
    handleRetiradaAtivaKeyDown,
    equipmentSearchType,
    setEquipmentSearchType,
    equipmentSearchReady,
  } = props;

  const { success: toastSuccess, error: toastError, Toaster } = useCxExpToast();
  const lastToastMessageRef = useRef(message || '');
  const skipInitialToastRef = useRef(true);

  useEffect(() => {
    if (skipInitialToastRef.current) {
      skipInitialToastRef.current = false;
      lastToastMessageRef.current = message || '';
      return;
    }
    if (!message || message === lastToastMessageRef.current) return;
    lastToastMessageRef.current = message;
    if (message.includes('sucesso')) toastSuccess(message);
    else toastError(message);
  }, [message, toastSuccess, toastError]);

  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const formSnapshotRef = useRef('');

  const [completion, setCompletion] = useState({ native: false, saved: '', value: '', name: '', source: '', newName: '' });
  const [completionLookup, setCompletionLookup] = useState(undefined);
  const [completionSaving, setCompletionSaving] = useState(false);
  const [recurrence, setRecurrence] = useState({
    native: false,
    days: '30',
    types: RECURRENCE_TYPE_KEYS,
    savedDays: '30',
    savedTypes: RECURRENCE_TYPE_KEYS,
  });
  const [recurrenceSaving, setRecurrenceSaving] = useState(false);

  const applyRecurrenceSaved = (data) => {
    const days = String(data?.windowDays ?? 30);
    const types = Array.isArray(data?.typeKeys) ? data.typeKeys.filter((k) => RECURRENCE_TYPE_KEYS.includes(k)) : RECURRENCE_TYPE_KEYS;
    setRecurrence({ native: true, days, types, savedDays: days, savedTypes: types });
  };

  const applyCompletionSaved = (data) => {
    const code = data?.erpCode || '';
    setCompletion({ native: true, saved: code, value: code, name: data?.name || '', source: data?.source || '', newName: data?.name || '' });
    setCompletionLookup(undefined);
  };

  useEffect(() => {
    let cancelled = false;
    fetchCompletionStatus()
      .then(({ nativeOperation, data }) => {
        if (!cancelled && nativeOperation) applyCompletionSaved(data);
      })
      .catch(() => {});
    fetchOpeningRecurrence()
      .then(({ nativeOperation, data }) => {
        if (!cancelled && nativeOperation) applyRecurrenceSaved(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!completion.native || completion.value.length !== 2 || completion.value === completion.saved) {
      setCompletionLookup(undefined);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      lookupCompletionStatus(completion.value)
        .then((found) => {
          if (cancelled) return;
          setCompletionLookup(found);
          setCompletion((prev) => ({ ...prev, newName: found?.name || '' }));
        })
        .catch(() => {});
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [completion.native, completion.value, completion.saved]);

  const completionIsSaved = completion.value === completion.saved;
  const completionCurrent = completionIsSaved
    ? (completion.saved ? { name: completion.name, source: completion.source } : null)
    : completionLookup;
  /** Nome editável: status novo (código ainda inexistente) ou status nativo já cadastrado. */
  const completionNameEditable =
    completion.native && completion.value.length === 2 && (completionCurrent === null || completionCurrent?.source === 'native');
  const completionDirty =
    completion.native &&
    (!completionIsSaved || (completionNameEditable && completion.newName.trim() !== (completion.name || '')));
  const recurrenceDirty =
    recurrence.native &&
    (recurrence.days !== recurrence.savedDays || !sameTypes(recurrence.types, recurrence.savedTypes));

  const operationalStatuses = [
    { id: 'pcs2_inservice', label: 'Em serviço', value: statusInservice, onChange: handleInserviceChange, icon: Wrench },
    { id: 'pcs2_deslocamento', label: 'Em deslocamento', value: statusDeslocamento, onChange: handleDeslocamentoChange, icon: Truck },
    {
      id: 'pcs2_retorno',
      label: 'Retorno de peças',
      value: statusRetorno,
      onChange: handleRetornoChange,
      icon: Package,
      disabled: retornoPorCondicaoIntervencao,
    },
    { id: 'pcs2_forward', label: 'Encaminhado ao técnico', value: statusForward, onChange: handleForwardChange, icon: Send },
    { id: 'pcs2_tomorrow', label: 'Próximo dia', value: statusTomorrow, onChange: handleTomorrowChange, icon: CalendarClock },
    { id: 'pcs2_uptodate', label: 'Em dia ou futura', value: statusUptodate, onChange: handleUptodateChange, icon: CalendarCheck },
    { id: 'pcs2_finish', label: 'Finalizado', value: statusFinish, onChange: handleFinishChange, icon: CheckCircle2 },
    ...(completion.native
      ? [
          {
            id: 'pcs2_completion',
            label: completionCurrent?.name
              ? `Status de conclusão · ${completionCurrent.name}${completionCurrent.source === 'native' ? ' (novo)' : ''}`
              : 'Status de conclusão',
            value: completion.value,
            onChange: (e) => {
              const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 2);
              setCompletion((prev) => ({ ...prev, value }));
            },
            icon: Flag,
          },
        ]
      : []),
  ];

  const handleSaveAll = async () => {
    if (recurrenceDirty) {
      const days = Number(recurrence.days);
      if (!Number.isInteger(days) || days < 1 || days > 120) {
        toastError('Reincidência: informe de 1 a 120 dias.');
        return;
      }
    }
    if (isDirty) await saveData();
    if (recurrenceDirty) {
      setRecurrenceSaving(true);
      try {
        const saved = await saveOpeningRecurrence(Number(recurrence.days), recurrence.types);
        applyRecurrenceSaved(saved);
        if (!isDirty && !completionDirty) toastSuccess('Reincidência salva com sucesso');
      } catch (err) {
        toastError(err.message);
        setRecurrenceSaving(false);
        return;
      }
      setRecurrenceSaving(false);
    }
    if (!completionDirty) return;
    if (completion.value.length !== 2) {
      toastError('Status de conclusão: informe 2 caracteres.');
      return;
    }
    if (completionCurrent === null && !completion.newName.trim()) {
      toastError('Status de conclusão: informe o nome do novo status.');
      return;
    }
    setCompletionSaving(true);
    try {
      const saved = await saveCompletionStatus(completion.value, completionNameEditable ? completion.newName.trim() : undefined);
      applyCompletionSaved(saved);
      if (!isDirty) toastSuccess('Status de conclusão salvo com sucesso');
    } catch (err) {
      toastError(err.message);
    } finally {
      setCompletionSaving(false);
    }
  };

  const formSnapshot = useMemo(
    () =>
      serializePcs2FormState({
        statusInservice,
        statusDeslocamento,
        statusRetorno,
        statusForward,
        statusTomorrow,
        statusUptodate,
        statusFinish,
        statusOpenTags,
        retiradaAtivaTags,
        enviarProximoDiaAutomatico,
        futureTomorrowSchedule,
        retornoPorCondicaoIntervencao,
        habilitarTrocaStatus,
        osCorretiva,
        osEstoque,
        osPreventiva,
        osAfericao,
        osDesinstalacao,
        osRecarga,
        osBalcao,
        automationStatusPedidoDisponivel,
        automationStatusOsEncaminhar,
        automationData,
        equipmentSearchType,
      }),
    [
      statusInservice,
      statusDeslocamento,
      statusRetorno,
      statusForward,
      statusTomorrow,
      statusUptodate,
      statusFinish,
      statusOpenTags,
      retiradaAtivaTags,
      enviarProximoDiaAutomatico,
      futureTomorrowSchedule,
      retornoPorCondicaoIntervencao,
      habilitarTrocaStatus,
      osCorretiva,
      osEstoque,
      osPreventiva,
      osAfericao,
      osDesinstalacao,
      osRecarga,
      osBalcao,
      automationStatusPedidoDisponivel,
      automationStatusOsEncaminhar,
      automationData,
      equipmentSearchType,
    ]
  );

  formSnapshotRef.current = formSnapshot;

  useEffect(() => {
    if (isLoadingAutomation || !equipmentSearchReady) return;
    setSavedSnapshot((prev) => (prev === null ? formSnapshotRef.current : prev));
  }, [isLoadingAutomation, equipmentSearchReady]);

  useEffect(() => {
    if (message?.includes('sucesso')) {
      setSavedSnapshot(formSnapshotRef.current);
    }
  }, [message]);

  const isDirty = savedSnapshot !== null && formSnapshot !== savedSnapshot;

  const osTypes = [
    { id: 'pcs2_corretiva', label: 'OS corretiva', value: osCorretiva, onChange: handleOsCorretivaChange, icon: ClipboardList },
    { id: 'pcs2_estoque', label: 'OS estoque', value: osEstoque, onChange: handleOsEstoqueChange, icon: Boxes },
    { id: 'pcs2_preventiva', label: 'OS preventiva', value: osPreventiva, onChange: handleOsPreventivaChange, icon: ShieldCheck },
    { id: 'pcs2_afericao', label: 'OS aferição', value: osAfericao, onChange: handleOsAfericaoChange, icon: Gauge },
    { id: 'pcs2_desinstalacao', label: 'OS desinstalação', value: osDesinstalacao, onChange: handleOsDesinstalacaoChange, icon: Unplug },
    { id: 'pcs2_recarga', label: 'OS recarga', value: osRecarga, onChange: handleOsRecargaChange, icon: BatteryCharging },
    { id: 'pcs2_balcao', label: 'OS balcão', value: osBalcao, onChange: handleOsBalcaoChange, icon: Store },
  ];

  return (
    <div className="pcs2-page">
        <Pcs2Section
          icon={Route}
          title="Configuração de processos"
          desc="Definição de status do processo de assistência técnica e automações"
        >
          <Pcs2Subblock title="Status operacionais" desc="Configure os status correspondentes a cada ponto do processo.">
            <div className="pcs2-card">
              <div className="pcs2-list">
                {operationalStatuses.map((item) => (
                  <Pcs2ListItem key={item.id} {...item} />
                ))}
                {completionNameEditable && (
                  <div className="pcs2-list-item pcs2-list-item--sub">
                    <span className="pcs2-list-label">
                      {completionCurrent === null ? 'Nome do novo status (só para OS nativa)' : 'Nome do status'}
                    </span>
                    <input
                      type="text"
                      className="pcs2-list-name"
                      maxLength={60}
                      value={completion.newName}
                      placeholder="Ex.: CONCLUÍDA"
                      onChange={(e) => {
                        const newName = e.target.value;
                        setCompletion((prev) => ({ ...prev, newName }));
                      }}
                      aria-label="Nome do status de conclusão"
                    />
                  </div>
                )}
              </div>
            </div>
          </Pcs2Subblock>

          <Pcs2Subblock hideHead>
            <div className="pcs2-card pcs2-card--flat">
              <Pcs2SettingRow
                title="Retorno de peças — automação"
                desc="Status quando o pedido de peças fica disponível e encaminha a OS ao técnico."
              >
                <Pcs2TooltipHost
                  text={retornoPorCondicaoIntervencao ? 'Desabilitado com retorno por condição de intervenção ativo' : 'Configurar automação de retorno de peças'}
                  align="end"
                >
                  <button
                    type="button"
                    className="pcs2-btn-gear"
                    onClick={() => setAutomationRetornoModalOpen(true)}
                    disabled={retornoPorCondicaoIntervencao}
                    aria-label="Configurar automação de retorno de peças"
                  >
                    <Settings size={15} strokeWidth={1.75} />
                  </button>
                </Pcs2TooltipHost>
              </Pcs2SettingRow>

              <Pcs2SettingRow
                title="Retorno por condição de intervenção"
                desc="Automatizar retorno de peças a OS por condição de intervenção"
                descInfo={PCS2_RETORNO_COND_INFO}
              >
                <Pcs2Switch
                  checked={retornoPorCondicaoIntervencao}
                  onChange={(e) => setRetornoPorCondicaoIntervencao(e.target.checked)}
                  ariaLabel="Retorno por condição de intervenção"
                />
              </Pcs2SettingRow>
            </div>

            <div className="pcs2-card pcs2-card--flat pcs2-card--spaced">
              <Pcs2SettingRow
                title="Enviar de próximo dia para roteiro de hoje — automação"
                desc="Agendamento e dias da semana para mover ordens automaticamente."
              >
                <Pcs2TooltipHost text="Configurar automação do próximo dia" align="end">
                  <button
                    type="button"
                    className="pcs2-btn-gear"
                    onClick={() => setAutomationProximoDiaModalOpen(true)}
                    aria-label="Configurar automação do próximo dia"
                  >
                    <Settings size={15} strokeWidth={1.75} />
                  </button>
                </Pcs2TooltipHost>
              </Pcs2SettingRow>

              <Pcs2SettingRow
                title="Enviar de Em dia ou futura para próximo dia — automação"
                desc="Altera status futuro para amanhã no horário configurado."
              >
                {enviarProximoDiaAutomatico && (
                  <Pcs2TooltipHost text="Horário de execução do agendamento" align="end">
                    <button
                      type="button"
                      className="pcs2-btn-gear"
                      aria-label="Horário de execução do agendamento"
                      onClick={() => setFutureTomorrowScheduleModalOpen(true)}
                    >
                      <Timer size={15} strokeWidth={1.75} />
                    </button>
                  </Pcs2TooltipHost>
                )}
                <Pcs2Switch
                  checked={enviarProximoDiaAutomatico}
                  onChange={(e) => setEnviarProximoDiaAutomatico(e.target.checked)}
                  ariaLabel="Enviar de Em dia ou futura para próximo dia — automação"
                  tooltip="Alteração automática de status futuro para amanhã"
                />
              </Pcs2SettingRow>
            </div>
          </Pcs2Subblock>

          <Pcs2StatusTagsBlock
            title="Status de OS em aberto"
            desc="Até 9 status exibidos na coluna em aberto."
            id="pcs2_status_open"
            tags={statusOpenTags}
            onAddEmpty={addEmptyStatusOpenTag}
            onRemove={removeStatusOpenTag}
            onUpdate={updateStatusOpenTag}
          />
        </Pcs2Section>

        <hr className="pcs2-divider" />

        <Pcs2Section icon={Ticket} title="Abertura de OS" desc="Tipos de ordem de serviço e validação de processo de retirada ativa.">
          <Pcs2Subblock title="Tipos de ordem de serviço" desc="Configure os status iniciais de processo por tipo de ordem de serviço.">
            <div className="pcs2-card">
              <div className="pcs2-list">
                {osTypes.map((item) => (
                  <Pcs2ListItem key={item.id} {...item} />
                ))}
              </div>
            </div>
          </Pcs2Subblock>

          <Pcs2StatusTagsBlock
            title="Retirada ativa"
            desc="Configure os status de procesos de retirada em andamento para trava em novas aberturas."
            id="pcs2_retirada_ativa"
            tags={retiradaAtivaTags}
            maxTags={null}
            onAddEmpty={addEmptyRetiradaAtivaTag}
            onRemove={removeRetiradaAtivaTag}
            onUpdate={updateRetiradaAtivaTag}
          />

          <Pcs2Subblock hideHead>
            <div className="pcs2-card pcs2-card--flat pcs2-card--spaced">
              <Pcs2SettingRow
                title="Busca pelo equipamento"
                desc="Define como localizar equipamentos na abertura de ordens de serviço."
              >
                <Pcs2SegmentedControl
                  ariaLabel="Busca pelo equipamento"
                  value={equipmentSearchType}
                  onChange={setEquipmentSearchType}
                  options={[
                    { value: 'serie-patrimonio', label: 'Série e patrimônio' },
                    { value: 'patrimonio-only', label: 'Somente patrimônio' },
                  ]}
                />
              </Pcs2SettingRow>
            </div>
          </Pcs2Subblock>

          {recurrence.native ? (
            <Pcs2Subblock title="Reincidência" desc="Quando uma OS fechada do mesmo equipamento marca a abertura nova.">
              <div className="pcs2-card pcs2-card--flat pcs2-card--spaced">
                <div className="pcs2-recurrence">
                  <Pcs2SettingRow
                    title="Tempo para considerar reincidência"
                    desc="Dias após o fechamento. No máximo 120."
                    descInfo={PCS2_RECURRENCE_INFO}
                  >
                    <input
                      type="text"
                      inputMode="numeric"
                      className="pcs2-days-input"
                      aria-label="Tempo para considerar reincidência em dias"
                      value={recurrence.days}
                      onChange={(e) => {
                        const days = e.target.value.replace(/\D/g, '').slice(0, 3);
                        setRecurrence((prev) => ({ ...prev, days }));
                      }}
                    />
                    <span className="pcs2-days-suffix">dias</span>
                  </Pcs2SettingRow>
                  <div className="pcs2-recurrence-types">
                    <div className="pcs2-setting-copy">
                      <span className="pcs2-setting-title">Tipos de OS</span>
                      <span className="pcs2-setting-desc">Os marcados entram nesta regra. Dentro do prazo acima, a abertura vira reincidência.</span>
                    </div>
                    <div className="pcs2-type-picks user-form-toggle-wrap" role="group" aria-label="Tipos de OS da reincidência">
                      {RECURRENCE_TYPES.map((type) => {
                        const on = recurrence.types.includes(type.key);
                        return (
                          <button
                            key={type.key}
                            type="button"
                            className={`user-form-toggle-pill${on ? ' active' : ''}`}
                            aria-pressed={on}
                            onClick={() => setRecurrence((prev) => ({
                              ...prev,
                              types: on ? prev.types.filter((k) => k !== type.key) : [...prev.types, type.key],
                            }))}
                          >
                            {type.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </Pcs2Subblock>
          ) : null}
        </Pcs2Section>

        <hr className="pcs2-divider" />

        <Pcs2Section icon={Lock} title="Fechamento de OS" desc="Opções ao encerrar ordens de serviço.">
          <Pcs2Subblock hideHead>
            <div className="pcs2-card pcs2-card--flat">
              <Pcs2SettingRow
                title="Habilitar troca de status"
                desc="Exibe opção de troca de status no fechamento da OS."
              >
                <Pcs2Switch
                  checked={habilitarTrocaStatus}
                  onChange={(e) => setHabilitarTrocaStatus(e.target.checked)}
                  ariaLabel="Habilitar troca de status"
                />
              </Pcs2SettingRow>
            </div>
          </Pcs2Subblock>
        </Pcs2Section>

      {Toaster}

      {createPortal(
        <button
          type="button"
          className="pcs2-save-fab"
          onClick={handleSaveAll}
          disabled={isSaving || completionSaving || recurrenceSaving || !(isDirty || completionDirty || recurrenceDirty)}
          aria-label="Salvar configuração"
        >
          {isSaving || completionSaving || recurrenceSaving ? <Loader2 size={14} className="pcs2-spin" aria-hidden /> : null}
          {isSaving || completionSaving || recurrenceSaving ? 'Salvando...' : 'Salvar configuração'}
        </button>,
        document.body
      )}

      <ProcessCfgModal
        open={automationRetornoModalOpen}
        onClose={() => setAutomationRetornoModalOpen(false)}
        title="Automação — Retorno de peças"
      >
        <p className="process-cfg-modal-hint">
          Status quando o pedido fica disponível e após encaminhar a OS. Use <strong>Salvar dados</strong> na tela para gravar.
        </p>
        <div className="process-cfg-modal-field">
          <label htmlFor="pcs2_automation_pedido">Status do pedido quando disponível</label>
          <input
            type="text"
            id="pcs2_automation_pedido"
            className={`process-cfg-modal-pin${automationStatusPedidoDisponivel.length === 1 ? ' process-cfg-modal-pin--error' : ''}`}
            maxLength={2}
            value={automationStatusPedidoDisponivel}
            onChange={handleAutomationStatusPedidoDisponivel}
            placeholder="00"
          />
          {automationStatusPedidoDisponivel.length === 1 && (
            <p className="process-cfg-modal-field-error">Informe 2 caracteres alfanuméricos.</p>
          )}
        </div>
        <div className="process-cfg-modal-field">
          <label htmlFor="pcs2_automation_encaminhar">Status da OS após encaminhar</label>
          <input
            type="text"
            id="pcs2_automation_encaminhar"
            className={`process-cfg-modal-pin${automationStatusOsEncaminhar.length === 1 ? ' process-cfg-modal-pin--error' : ''}`}
            maxLength={2}
            value={automationStatusOsEncaminhar}
            onChange={handleAutomationStatusOsEncaminhar}
            placeholder="00"
          />
          {automationStatusOsEncaminhar.length === 1 && (
            <p className="process-cfg-modal-field-error">Informe 2 caracteres alfanuméricos.</p>
          )}
        </div>
      </ProcessCfgModal>

      <ProcessCfgModal
        open={automationProximoDiaModalOpen}
        onClose={() => setAutomationProximoDiaModalOpen(false)}
        title="Automação — Para atender no próximo dia"
      >
        <div className={`process-cfg-modal-automation-panel${automationError ? ' process-cfg-modal-automation-panel--error' : ''}`}>
          {isLoadingAutomation ? (
            <p className="process-cfg-modal-hint" style={{ textAlign: 'center', padding: '12px 0' }}>
              Carregando configuração...
            </p>
          ) : (
            <>
              <p className="process-cfg-modal-hint">
                Agendamento automático. Alterações entram ao clicar em <strong>Salvar dados</strong>.
              </p>
              <div className="process-cfg-modal-field">
                <label>Dias da semana</label>
                <div className="process-cfg-modal-week-grid">
                  {WEEK_ROWS.map(({ key, label }) => (
                    <label key={key}>
                      <input
                        type="checkbox"
                        checked={automationData[key] || false}
                        onChange={() => handleAutomationDayChange(key)}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="process-cfg-modal-field">
                <label htmlFor="pcs2_automation_schedule">Horário (hh:mm)</label>
                <input
                  type="text"
                  id="pcs2_automation_schedule"
                  className="process-cfg-modal-pin"
                  style={{ width: '72px' }}
                  value={automationData.schedule || ''}
                  onChange={handleAutomationScheduleChange}
                  placeholder="hh:mm"
                  maxLength={5}
                />
                {automationData.schedule && !validateSchedule(automationData.schedule).valid && (
                  <p className="process-cfg-modal-field-error">{validateSchedule(automationData.schedule).message}</p>
                )}
              </div>
              <div className="process-cfg-modal-field">
                <label htmlFor="pcs2_automation_tipo">Tipo de programação</label>
                <select
                  id="pcs2_automation_tipo"
                  className="process-cfg-modal-select"
                  style={{ width: '100%', maxWidth: '100%' }}
                  value={automationData.tipoagend || 1}
                  onChange={handleAutomationTipoagendChange}
                >
                  <option value={1}>Apenas ordens roteirizadas</option>
                  <option value={2}>Todas as ordens em PREVISTO PARA AMANHÃ</option>
                </select>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                <button
                  type="button"
                  className="process-cfg-modal-report-btn"
                  onClick={generateLogsReport}
                  disabled={isGeneratingReport}
                >
                  {isGeneratingReport ? 'Gerando...' : 'Relatório de logs'}
                </button>
              </div>
            </>
          )}
        </div>
      </ProcessCfgModal>

      <ProcessCfgModal
        open={futureTomorrowScheduleModalOpen}
        onClose={() => setFutureTomorrowScheduleModalOpen(false)}
        title="Horário de execução do agendamento"
      >
        <p className="process-cfg-modal-hint">
          Formato 24h (hh:mm), entre <strong>00:01</strong> e <strong>05:00</strong>. Gravação via <strong>Salvar dados</strong>.
        </p>
        <div className="process-cfg-modal-field">
          <label htmlFor="pcs2_future_schedule">Horário</label>
          <input
            id="pcs2_future_schedule"
            type="time"
            step={60}
            min="00:01"
            max="05:00"
            className="process-cfg-modal-time"
            value={futureTomorrowSchedule || '04:30'}
            onChange={handleFutureTomorrowScheduleChange}
          />
          {futureTomorrowSchedule && !validateFutureTomorrowSchedule(futureTomorrowSchedule).valid && (
            <p className="process-cfg-modal-field-error">
              {validateFutureTomorrowSchedule(futureTomorrowSchedule).message}
            </p>
          )}
        </div>
      </ProcessCfgModal>
    </div>
  );
}
